import React, { useRef } from "react"
import PropTypes from "prop-types"
import Table from "components/Table"
import useScrollPosition from "hooks/useScrollPosition"
import theme from "styles/theme"
import useElementDimensions from "hooks/useElementDimensions"
import useDragScrolling from "hooks/useDragScrolling"

// Distance in pixels to scroll before showing shadow
const SCROLL_MIN = 3

const componentShouldUpdate = (prev, curr) =>
  (prev.x >= SCROLL_MIN && curr.x < SCROLL_MIN) ||
  (prev.x < SCROLL_MIN && curr.x >= SCROLL_MIN)

const ResponsiveTable = ({ headings, rows }) => {
  const wrapper = useRef()
  const tableRef = useRef()
  const { x } = useScrollPosition(wrapper, componentShouldUpdate)
  const { width: wrapperWidth = 500 } = useElementDimensions(wrapper, 100) || {}
  const { width: tableWidth = 500 } = useElementDimensions(tableRef, 100) || {}
  const { dragging, dragEvents } = useDragScrolling(wrapper)
  const scrollable = tableWidth > wrapperWidth

  return (
    <div
      ref={wrapper}
      css={{
        position: "relative",
        maxWidth: "calc(100vw - var(--inlinePadding))",
        marginRight: scrollable
          ? `calc((-1 * (100vw - 100%) / 2))`
          : `calc(-1 * var(--inlinePadding))`,
        overflowX: "auto",
        overflowY: "hidden",
        scrollbarWidth: "none",
        WebkitOverflowScrolling: "touch",
        userSelect: "none",
        "::-webkit-scrollbar": {
          display: "none",
          width: 0,
          height: 0,
          background: "transparent",
        },
        [theme.tablet]: {
          marginRight: `calc(-1 * var(--inlinePadding))`,
        },
      }}
    >
      <Table
        ref={tableRef}
        headings={headings}
        rows={rows}
        css={theme.expandQueries(
          {
            cursor: scrollable ? (dragging ? "grabbing" : "grab") : "initial",
            minWidth: minTableWidth(headings),
            th: {
              transition: "box-shadow 500ms",
              ":first-of-type": {
                position: "sticky",
                left: 0,
                minWidth: [220, 160, 110],
                boxShadow:
                  x >= SCROLL_MIN && "2px 2px 4px rgba(86, 90, 92, 0.3)",
                [theme.mobilePortrait]: { maxWidth: "33vw" },
              },
            },
          },
          ["root", theme.mobile, theme.smallMobilePortrait]
        )}
        {...dragEvents}
      />
    </div>
  )
}

export default ResponsiveTable

ResponsiveTable.propTypes = {
  headings: PropTypes.arrayOf(PropTypes.string).isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      mode: PropTypes.oneOf(["row", "heading"]),
      cells: PropTypes.array.isRequired,
    }).isRequired
  ),
}

const minTableWidth = headings => {
  const contentLength = headings.reduce(
    (sum, heading) => sum + heading.length,
    0
  )
  const minPx = Math.min(
    // Largest of…
    Math.max(
      // …heading length based size…
      contentLength * 7,
      // …column count based size…
      headings.length * 120,
      // …and a fixed minimum…
      600
    ),

    // …capped at a reasonable minimum width
    1200
  )

  // Converted to ems based on the desktop font-size
  return `${Math.round(minPx / 14)}em`
}
